import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ZoomIn, ZoomOut, RotateCw, Maximize2, Loader2, Send, Search } from "lucide-react";
import { api } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Input } from "./ui/input";
import { personMatch } from "../lib/searchText";

// Keep number runs like 81-00000432 in their written order inside Arabic titles.
export const ltrNums = (t) => String(t || "").replace(/[\u2066-\u2069]/g, "").replace(/(\d[\d\-/.]*\d)/g, "\u2066$1\u2069");

const STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3, 4];
const TB = "grid h-8 w-8 place-items-center rounded-lg text-slate-600 transition-colors hover:bg-white hover:text-slate-900 disabled:opacity-30";

// Zoom / rotate toolbar + scrollable viewer for one image or several PDF page images.
export const ZoomableView = ({ srcs = [], alt = "", maxH = "max-h-[68vh]", extra = null }) => {
  const [i, setI] = useState(2);
  const [rot, setRot] = useState(0);
  const [ratio, setRatio] = useState({});
  const sig = `${srcs.length}:${srcs[0] || ""}`;
  useEffect(() => { setI(2); setRot(0); }, [sig]);
  const z = STEPS[i];
  const side = rot % 180 !== 0;
  return (
    <div className="space-y-2" data-testid="zoomable-view">
      <div className="flex flex-wrap items-center gap-1 rounded-xl bg-slate-100 p-1" dir="ltr">
        <button type="button" className={TB} onClick={() => setI((x) => Math.max(0, x - 1))} disabled={i === 0} title="تصغير" data-testid="zoom-out-btn"><ZoomOut className="h-4 w-4" /></button>
        <span className="w-12 text-center text-xs font-semibold tabular-nums text-slate-600" data-testid="zoom-level">{Math.round(z * 100)}%</span>
        <button type="button" className={TB} onClick={() => setI((x) => Math.min(STEPS.length - 1, x + 1))} disabled={i === STEPS.length - 1} title="تكبير" data-testid="zoom-in-btn"><ZoomIn className="h-4 w-4" /></button>
        <span className="mx-1 h-5 w-px bg-slate-300" />
        <button type="button" className={TB} onClick={() => setRot((r) => (r + 90) % 360)} title="تدوير" data-testid="rotate-btn"><RotateCw className="h-4 w-4" /></button>
        <button type="button" className={TB} onClick={() => { setI(2); setRot(0); }} title="إعادة الضبط" data-testid="zoom-reset-btn"><Maximize2 className="h-4 w-4" /></button>
        {extra && <div className="ms-auto">{extra}</div>}
      </div>
      <div className={`${maxH} overflow-auto rounded-lg border border-slate-200 bg-slate-100 p-2`} data-testid="zoomable-scroll">
        <div className="mx-auto space-y-2" style={{ width: `${z * 100}%` }}>
          {srcs.map((src, k) => {
            const r = ratio[k] || 1; // natural width / height
            const img = { onDoubleClick: () => setI((x) => (x === 2 ? 5 : 2)), draggable: false, src, alt: srcs.length > 1 ? `صفحة ${k + 1}` : alt,
              onLoad: (e) => { const { naturalWidth: w, naturalHeight: h } = e.currentTarget; if (w && h) setRatio((m) => ({ ...m, [k]: w / h })); } };
            return side ? (
              <div key={k} className="relative overflow-hidden" style={{ aspectRatio: `${1 / r}` }}>
                <img {...img} className="absolute left-1/2 top-1/2 block max-w-none rounded bg-white shadow-sm transition-transform duration-200"
                  style={{ width: `${r * 100}%`, transform: `translate(-50%, -50%) rotate(${rot}deg)` }} />
              </div>
            ) : (
              <img key={k} {...img} className="block w-full rounded bg-white shadow-sm transition-transform duration-200" style={{ transform: `rotate(${rot}deg)` }} />
            );
          })}
        </div>
      </div>
    </div>
  );
};

const extFor = (type) => (type?.includes("pdf") ? ".pdf" : type?.includes("png") ? ".png" : type?.includes("webp") ? ".webp" : ".jpg");

// Sends the attachment to an employee's WhatsApp. If employeeId is given it goes straight to him.
export const WaShareButton = ({ attachmentId, name, mime, employeeId, employeeName }) => {
  const [open, setOpen] = useState(false);
  const [emps, setEmps] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => {
    if (open && !employeeId && !emps) api.listEmployees().then((r) => setEmps((Array.isArray(r) ? r : r.items || []).filter((e) => e.phone || e.notify_phone))).catch(() => setEmps([]));
  }, [open, employeeId, emps]);
  const shown = useMemo(() => (emps || []).filter((e) => personMatch(e, q)).slice(0, 40), [emps, q]);
  const send = async (id, who) => {
    setBusy(id);
    try {
      const clean = (name || "مرفق").replace(/[\u2066-\u2069\u200e\u200f]/g, "");
      const base = clean.replace(/[\\/:*?"<>|]/g, " ").trim();
      const filename = /\.[a-z0-9]{2,5}$/i.test(base) ? base : base + extFor(mime);
      const r = await api.waSend({ employee_ids: [id], body: `📎 ${clean}`, attachment_id: attachmentId, filename, ref_type: "attachment_share" });
      if (r.sent) toast.success(`تم إرسال المرفق على واتساب ${who}`);
      else toast.error(r.no_phone_count ? `لا يوجد رقم جوال لـ ${who}` : r.failed?.[0]?.reason || "تعذّر الإرسال");
      setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإرسال"); } finally { setBusy(""); }
  };
  const btn = "inline-flex items-center gap-1.5 rounded-lg bg-[#25D366] px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-[#1ebe5b] disabled:opacity-60";
  if (employeeId) {
    return (
      <button type="button" className={btn} disabled={!!busy} data-testid="wa-share-btn"
        onClick={() => window.confirm(`إرسال «${name || "المرفق"}» على واتساب ${employeeName || "الموظف"}؟`) && send(employeeId, employeeName || "الموظف")}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} واتساب الموظف
      </button>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild><button type="button" className={btn} data-testid="wa-share-btn"><Send className="h-3.5 w-3.5" /> إرسال واتساب</button></PopoverTrigger>
      <PopoverContent align="end" collisionPadding={12} sideOffset={6} className="w-72 p-2 font-body" dir="rtl" data-testid="wa-share-popover">
        <div className="relative mb-2"><Search className="absolute right-2 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث باسم الموظف أو الإقامة" className="h-9 pr-8 text-sm" data-testid="wa-share-search" /></div>
        <div className="max-h-60 overflow-y-auto">
          {!emps ? <div className="py-6 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin text-slate-400" /></div>
            : shown.length === 0 ? <p className="py-6 text-center text-xs text-slate-400">لا توجد نتائج</p>
              : shown.map((e) => (
                <button key={e.id} type="button" disabled={!!busy} onClick={() => send(e.id, e.name)} data-testid={`wa-share-emp-${e.id}`}
                  className="flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-right text-sm hover:bg-slate-50">
                  <span className="truncate">{e.name}</span>{busy === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <span className="text-[10px] text-slate-400">{e.branch}</span>}
                </button>))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
